import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { Module } from 'vuex'
import { serializer } from "@/api/shared/serializer";
import { AuditLog } from '@/models/logs/AuditLog';
import { _filterLogs, _formatLog, _getInitiatorCollection } from '@/utils/auditLogs';
import { FamilyMembersLogsTypes } from "@/enums/FamilyMembersLogs"
import * as Sentry from "@sentry/vue";

const auditLogsModule: Module<any, any> = {
  namespaced: false,
  state: {
    auditLogs: [],
    familyMembersAuditLogs: [],
    familyMembersCareLogs: []
  },
  mutations: {
    setAuditLogs(state, data: AuditLog[]) {
      state.auditLogs = data;
    },
    setFamilyMembersAuditLogs(state, data: AuditLog[]) {
      state.familyMembersAuditLogs = data;
    },
    setFamilyMembersCareLogs(state, data: AuditLog[]) {
      state.familyMembersCareLogs = data;
    }
  },
  actions: {
    async getAuditLogs({ dispatch, commit }, { startDate, endDate }) {
      await firebase.firestore().collection("auditLogs")
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .where('initiatorType', '==', 'staff')
        .get()
        .then(async (querySnapshot) => {
          const auditLogs: AuditLog[] = serializer.querySnapshotToDocuments(querySnapshot);
          // Filter out logs with auditLogsUIVisibility: false (hidden batch operation logs)
          const visibleLogs = auditLogs.filter(log => log.auditLogsUIVisibility !== false);
          const auditLogsInitiators = await dispatch("getAuditLogsInitiators", visibleLogs);
          const auditLogsDocs = await dispatch("getAuditLogsDocs", visibleLogs);
          const filteredLogs = _filterLogs(visibleLogs);
          const result = filteredLogs.map(log => _formatLog(log, auditLogsInitiators, auditLogsDocs));
          commit('setAuditLogs', result);
        })
        .catch((error: unknown) => {
          console.error('[getAuditLogs] Error:', error);
          Sentry.captureException(error);
        })
    },
    async getAuditLogsInitiators(_, auditLogs: AuditLog[]) {
      const initiators = auditLogs
        .filter(log => log.initiatorId && typeof log.initiatorId === "string" && log.initiatorType !== "unknown")
        .map(x => ({ 
          id: x.initiatorId,
          logId: x.id,
          initiatorAccessLevel: x.initiatorAccessLevel, 
          initiatorName: x.initiatorName, 
          collection: _getInitiatorCollection(x.initiatorType) 
        }));

      const results = await Promise.all(
        initiators.map(async ({ id, collection, initiatorAccessLevel, initiatorName, logId }) => {
          try {
            if (initiatorAccessLevel === 'SuperAdmin') {
              return { id, name: initiatorName ?? '' };
            }
            
            const docRef = firebase.firestore().collection(collection).doc(id);
            const doc = await docRef.get();

            if (!doc.exists) {
              return { id, name: initiatorName ?? 'Unknown' };
            }

            const data = doc.data();
            return { id: doc.id, name: data?.name ?? initiatorName ?? 'Unknown' };
          } catch (err) {
            Sentry.captureException(err, {
              extra: { logId, collection, initiatorId: id, initiatorAccessLevel }
            });
            return { id, name: initiatorName ?? 'Unknown' };
          }
        })
      );

      return results;
    },
    async getAuditLogsDocs(_, auditLogs: AuditLog[]) {
      const docsChanged = auditLogs.filter(log => log.docId).map(x => { return { id: x.docId, collection: x.collection }});
      
      const results = await Promise.all(
        docsChanged.map(async ({ collection, id }) => {
          try {
            const doc = await firebase.firestore().collection(collection).doc(id).get();
            
            if (!doc.exists) {
              return { id, name: '' };
            }
            
            const data = doc.data();
            const name = data?.name ?? data?.title ?? data?.residentName ?? '';
            return { id: doc.id, name };
          } catch (err) {
            Sentry.captureException(err, {
              extra: { collection, docId: id }
            });
            return { id, name: '' };
          }
        })
      );

      return results;
    },
    async getFamilyMembersAuditLogs({ dispatch, commit }, { startDate, endDate }) {
      await firebase.firestore().collection("auditLogs")
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .get()
        .then(async (querySnapshot) => {
          const auditLogs: AuditLog[] = serializer.querySnapshotToDocuments(querySnapshot);
          
          const auditLogsFamilyMembers = await dispatch("getAuditLogsFamilyMembers", auditLogs);
          const result = auditLogs.map(log => {
            const initiator = auditLogsFamilyMembers.find(x => x.initiatorId === log.initiatorId);
            return {
              ...log,
              ...initiator,
            }
          });
          commit('setFamilyMembersAuditLogs', result);
        })
        .catch((error: unknown) => {
          console.log("ERROR ", error);
        })
    },
    async getFamilyMemberCareLogs({ dispatch, commit }, { startDate, endDate }) {
      await firebase
        .firestore()
        .collection("auditLogs")
        .where('facilityId', '==', this.state.authModule.currentFacility)
        .where('collection', '==', 'careLogs')
        .where('timestamp', '>=', startDate)
        .where('timestamp', '<=', endDate)
        .get()
        .then(async (querySnapshot) => {
          const careLogs: AuditLog[] = serializer.querySnapshotToDocuments(querySnapshot);
          
          const careLogsFamilyMembers = await dispatch("getAuditLogsFamilyMembers", careLogs);
          const result = careLogs.map(log => {
            const initiator = careLogsFamilyMembers.find(x => x.initiatorId === log.initiatorId);

            return {
              ...log,
              ...initiator,
              typesOfRecordChanged: log.typesOfRecordChanged
            }
          });

          commit("setFamilyMembersCareLogs", result);
        })
        .catch((error: unknown) => {
          console.log("ERROR ", error);
        })
    },
    async getAuditLogsFamilyMembers(_, logs: AuditLog[]) {
      const familyMembersIds = [
        ...new Set(
          logs.map((x) => x.initiatorId)
        ),
      ];
      const promises = familyMembersIds.map(id => firebase.firestore().collection("familyHubUsers").doc(id).get());
      const docs = await Promise.all(promises);

      return docs.map(doc => ({
        initiatorId: doc.id,
        initiatorName: doc.data()?.name ?? '',
        initiatorEPOA: doc.data()?.isFinancialEPOA ?? false,
        initiatorResidents: doc.data()?.residents ?? [],
        source: FamilyMembersLogsTypes.Care,
      }));
    },
  }
}
export default auditLogsModule;